import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import Masonry from 'react-masonry-css';
import { getPups, getFeaturedPup } from '../services/pupService';
import { Pup } from '../types/breed';
import PupCard from '../components/PupCard';
import FeaturedPupCard from '../components/FeaturedPupCard';

const HomePage: React.FC = () => {
  const [pups, setPups] = useState<Pup[]>([]);
  const [featuredPup, setFeaturedPup] = useState<Pup | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [visiblePups, setVisiblePups] = useState<number>(12);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  // Masonry breakpoints for responsive columns
  const breakpointColumnsObj = {
    default: 4,
    1200: 3,
    768: 2,
    500: 1
  };

  useEffect(() => {
    const fetchPups = async () => {
      try {
        setLoading(true);
        const [data, featured] = await Promise.all([getPups(), getFeaturedPup()]);
        setPups(data);
        setFeaturedPup(featured);
        setError(null);
      } catch (e) {
        setError('Failed to load pups. Please try again later.');
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchPups();
  }, []);

  // Infinite scroll functionality
  useEffect(() => {
    const handleScroll = () => {
      // Check if user has scrolled to bottom
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      
      // If user is within 200px of bottom and there are more pups to load
      if (scrollTop + clientHeight >= scrollHeight - 200 && 
          !isLoadingMore && 
          visiblePups < pups.length) {
        setIsLoadingMore(true);
        
        // Add a small delay to prevent too rapid loading
        setTimeout(() => {
          setVisiblePups(prev => prev + 12);
          setIsLoadingMore(false);
        }, 300);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, visiblePups, pups.length]);

  return (
    <div>
      <section className="text-center py-5 bg-light mb-4 rounded">
        <h1>Welcome to PupNet</h1>
        <p className="lead">
          Join the hunt to capture all dog breeds in the wild.
        </p>
        <div className="mt-4">
          <Link to="/upload">
            <Button variant="primary" size="lg" className="me-2">
              Upload a Pup
            </Button>
          </Link>
          <Link to="/breeds">
            <Button variant="outline-primary" size="lg">
              Browse Breeds
            </Button>
          </Link>
        </div>
      </section>

      {loading ? (
        <p className="text-center">Loading pups...</p>
      ) : error ? (
        <p className="text-center text-danger">{error}</p>
      ) : (
        <>
          {pups.length === 0 ? (
            <p className="text-center">No pups have been posted yet. Be the first!</p>
          ) : (
            <Masonry
              breakpointCols={breakpointColumnsObj}
              className="masonry-grid"
              columnClassName="masonry-grid-column"
            >
              {featuredPup && (
                <FeaturedPupCard key={`featured-${featuredPup.id}`} pup={featuredPup} />
              )}
              {pups.filter(pup => !featuredPup || pup.id !== featuredPup.id).slice(0, visiblePups).map((pup) => (
                <PupCard key={pup.id} pup={pup} />
              ))}
            </Masonry>
          )}

          {isLoadingMore && (
            <div className="text-center mt-4">
              <div className="loading-spinner"></div>
              <p className="mt-2 text-muted">Loading more pups...</p>
            </div>
          )}
          
          {!isLoadingMore && pups.length > 0 && visiblePups >= pups.length && (
            <div className="text-center mt-4">
              <p className="text-muted">🐾 You've seen all the pups! 🐾</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HomePage;
